"use client";

import { IntegrationLogos } from "@/components/landing/IntegrationLogos";
import { Icon, type IconNameExtended } from "@/components/library/Icon";
import { getCollectionsIntegrationLogos } from "@/constants/collectionsIntegrationLogos";
import {
  getInsuranceIntegrationIcon,
  getInsuranceIntegrationLogos,
} from "@/constants/insuranceIntegrationLogos";
import {
  asIconName,
  asIconType,
  joinClasses,
  SECTION_BG,
} from "@/puck/landing/shared";
import type {
  LpIntegrationsBlockProps,
  LpOneGailBlockProps,
} from "@/puck/types";

export function LpOneGailBlock({
  eyebrow,
  titleLead,
  titleAccent,
  subtitle,
  background,
  channels,
  languagesEyebrow,
  languagesTitle,
  languagesDescription,
  languages,
  personalityEyebrow,
  personalityTitle,
  personalities,
}: Readonly<LpOneGailBlockProps>) {
  const isVoiceCard = background === "voiceCard";
  const channelCardBg = isVoiceCard ? "bg-white" : "bg-marketing-voice-card";
  const personalityBarBg = isVoiceCard ? "bg-white" : "bg-marketing-voice-card";
  const personalityChipBg = isVoiceCard
    ? "bg-marketing-voice-card"
    : "bg-white";

  return (
    <section
      className={joinClasses(
        "w-full border-t border-black/10 px-4 pt-20 pb-12 md:pt-28",
        isVoiceCard && "bg-marketing-voice-card",
      )}
    >
      <div className="mx-auto w-full max-w-3xl text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
          {titleLead} <span className="text-gail-primary">{titleAccent}</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl font-monument text-base leading-relaxed text-gail-gray md:text-lg">
          {subtitle}
        </p>
      </div>

      <div className="mx-auto mt-12 grid w-full max-w-6xl grid-cols-1 gap-4 md:mt-16 lg:grid-cols-2">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {channels.map((channel, index) => (
            <div
              key={`channel-${index}`}
              className={joinClasses(
                "flex flex-col rounded-2xl border border-black/5 px-6 py-6",
                channelCardBg,
                channel.wide && "sm:col-span-2",
              )}
            >
              <span
                className={joinClasses(
                  "flex h-9 w-9 items-center justify-center rounded-lg text-white",
                  channel.accent === "whatsapp"
                    ? "bg-[#25D366]"
                    : "bg-gail-primary",
                )}
              >
                <Icon
                  icon={asIconName(channel.iconName)}
                  type={asIconType(channel.iconType)}
                  className="h-5 w-5"
                />
              </span>
              <h3 className="mt-4 font-monument text-base font-bold tracking-tight text-black">
                {channel.title}
              </h3>
              <p className="mt-2 font-monument text-sm leading-relaxed text-gail-gray">
                {channel.description}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-col rounded-2xl bg-gail-primary px-7 py-7 md:px-8 md:py-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white">
            {languagesEyebrow}
          </p>
          <h3 className="mt-4 font-monument text-2xl font-bold tracking-tight text-white md:text-3xl">
            {languagesTitle}
          </h3>
          <p className="mt-3 font-monument text-sm leading-relaxed text-white md:text-[15px]">
            {languagesDescription}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {languages.map((language, index) => (
              <div
                key={`language-${index}`}
                className="flex items-center justify-between rounded-lg bg-white/10 px-4 py-3"
              >
                <span className="font-monument text-sm text-white">
                  {language.name}
                </span>
                {language.code ? (
                  <span className="font-mono text-[11px] tracking-widest text-white">
                    {language.code}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        className={joinClasses(
          "mx-auto mt-4 w-full max-w-6xl rounded-2xl border border-black/5 px-6 py-6 md:px-8 md:py-8",
          personalityBarBg,
        )}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
          <div className="lg:max-w-xs lg:shrink-0">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
              {personalityEyebrow}
            </p>
            <p className="mt-2 font-monument text-base font-bold tracking-tight text-black">
              {personalityTitle}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {personalities.map((personality, index) => (
              <span
                key={`personality-${index}`}
                className={joinClasses(
                  "inline-flex items-center gap-2.5 rounded-full border border-black/5 py-1.5 pl-1.5 pr-4",
                  personalityChipBg,
                )}
              >
                <span
                  className={joinClasses(
                    "flex h-7 w-7 items-center justify-center rounded-full font-mono text-[11px] font-medium text-white",
                    personality.avatar === "navy"
                      ? "bg-[#0a1f44]"
                      : "bg-gail-primary",
                  )}
                >
                  {personality.initial}
                </span>
                <span className="font-monument text-sm font-bold text-black">
                  {personality.name}
                </span>
                <span className="font-monument text-sm text-gail-gray">
                  {personality.traits}
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function LpIntegrationsBlock({
  eyebrow,
  titleLead,
  titleAccent,
  subtitle,
  featuredBadge,
  background,
  logoSet,
  items,
}: Readonly<LpIntegrationsBlockProps>) {
  const resolveLogos =
    logoSet === "insurance"
      ? getInsuranceIntegrationLogos
      : getCollectionsIntegrationLogos;
  const featuredSpan =
    logoSet === "insurance" ? "sm:col-span-2 lg:col-span-1" : "sm:col-span-2";

  return (
    <section
      className={joinClasses(
        "w-full border-t border-black/10 px-4 pt-20 pb-20 md:pt-28 md:pb-28",
        SECTION_BG[background],
      )}
    >
      <div className="mx-auto w-full max-w-3xl text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
          {eyebrow}
        </p>
        <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
          {titleLead} <span className="text-gail-primary">{titleAccent}</span>
        </h2>
        <p className="mx-auto mt-6 max-w-xl font-monument text-base leading-relaxed text-gail-gray md:text-lg">
          {subtitle}
        </p>
      </div>

      <div className="mx-auto mt-12 grid w-full max-w-5xl grid-cols-1 gap-4 md:mt-16 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((integration, index) => {
          const logos = resolveLogos(integration.name);
          const fallbackIcon =
            logoSet === "insurance"
              ? getInsuranceIntegrationIcon(integration.name)
              : null;

          return integration.featured ? (
            <div
              key={`integration-${index}`}
              className={joinClasses(
                "flex flex-col rounded-2xl bg-gail-primary px-6 py-6",
                featuredSpan,
              )}
            >
              <IntegrationLogos logos={logos} featured />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-monument text-base font-bold tracking-tight text-white">
                    {integration.name}
                  </h3>
                  <p className="mt-1 font-mono text-[13px] text-white">
                    {integration.category}
                  </p>
                </div>
                <span className="rounded-full bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-gail-primary">
                  {featuredBadge}
                </span>
              </div>
              {integration.tags.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {integration.tags.map((tag, tagIndex) => (
                    <span
                      key={`tag-${tagIndex}`}
                      className="rounded-md bg-white px-2.5 py-1 font-mono text-[11px] text-gail-primary"
                    >
                      {tag.text}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div
              key={`integration-${index}`}
              className="flex flex-col rounded-2xl border border-black/5 bg-white px-6 py-6"
            >
              <IntegrationLogos
                logos={logos}
                icon={
                  fallbackIcon
                    ? {
                        icon: fallbackIcon.icon as IconNameExtended,
                        type: fallbackIcon.type,
                      }
                    : null
                }
              />
              <h3 className="font-monument text-base font-bold tracking-tight text-black">
                {integration.name}
              </h3>
              <p className="mt-1 font-mono text-[13px] text-gail-gray">
                {integration.category}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
}
